/**
 * Tela do plano de saúde e da agenda de médicos.
 *
 * Papel: junta as duas seções do módulo — as apólices e os médicos —, cada uma
 * com a sua tabela, o seu formulário em caixa de diálogo e a sua confirmação de
 * exclusão.
 *
 * Como usar: as duas ficam na mesma tela porque é assim que se leem — a
 * carteirinha e o médico que a usa são a mesma consulta. Quem tem nível `ver`
 * não recebe `onNovo`, `onEditar` nem `onRemover`, e por isso não vê botão de
 * ação nenhum; recebe `podeAlterarFoto` falso, e o modal da carteirinha abre
 * sem o "Alterar foto".
 *
 * O subtítulo da seção de planos é montado dos planos carregados: as
 * operadoras distintas, depois do travessão os apelidos dos titulares, as duas
 * enumerações por `formatarListaEmCaixaAlta`. Sem plano nenhum, a seção fica
 * sem subtítulo, em vez de mostrar um travessão sem nada dos dois lados.
 *
 * O aviso de vencimento das apólices não fica aqui: ele mora na tela inicial,
 * ao lado do dos documentos, e `apurarVencimentosDePlanos` sai pelo `index.ts`
 * deste módulo para que `app/` possa montá-lo.
 */
import { type ContatoMedico, type PlanoSaude, acessoAlcanca } from '@concierge/shared';
import { useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import { formatarListaEmCaixaAlta } from '@/shared/lib/formato';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { SecaoCadastro } from '@/shared/ui/secao-cadastro';
import { FormularioMedico } from '../components/formulario-medico';
import { FormularioPlano } from '../components/formulario-plano';
import { TabelaMedicos } from '../components/tabela-medicos';
import { TabelaPlanos } from '../components/tabela-planos';
import {
  useExcluirMedico,
  useExcluirPlano,
  useMedicos,
  usePlanosSaude,
} from '../hooks/usar-saude';

/** Em que estado a caixa do formulário está: fechada, criando ou editando algo. */
type PainelAberto =
  | { modo: 'fechado' }
  | { modo: 'novo-plano' }
  | { modo: 'edicao-plano'; plano: PlanoSaude }
  | { modo: 'novo-medico' }
  | { modo: 'edicao-medico'; medico: ContatoMedico };

/** O que está esperando confirmação de exclusão. */
type AguardandoRemocao =
  { tipo: 'plano'; plano: PlanoSaude } | { tipo: 'medico'; medico: ContatoMedico };

/**
 * Página de Saúde.
 *
 * @returns A tela com as duas seções, a caixa do formulário e a confirmação.
 */
export function PaginaSaude() {
  const [painel, setPainel] = useState<PainelAberto>({ modo: 'fechado' });
  const [aRemover, setARemover] = useState<AguardandoRemocao | null>(null);
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'saude', 'editar');

  const consultaPlanos = usePlanosSaude();
  const consultaMedicos = useMedicos();
  const excluirPlano = useExcluirPlano();
  const excluirMedico = useExcluirMedico();
  const hoje = hojeEmIso();
  const planos = consultaPlanos.data ?? [];
  const operadoras = formatarListaEmCaixaAlta(planos.map((plano) => plano.operadora));
  const titulares = formatarListaEmCaixaAlta(planos.map((plano) => plano.pessoa.apelido));
  const subtituloDosPlanos = planos.length === 0 ? undefined : `${operadoras} — ${titulares}`;

  const removendo = excluirPlano.isPending || excluirMedico.isPending;

  /** Fecha a caixa, seja pelo botão, pelo `Esc` ou por clique no véu em volta dela. */
  function handlePainelOpenChange(aberto: boolean) {
    if (!aberto) setPainel({ modo: 'fechado' });
  }

  /** Abre a confirmação de exclusão, limpando a falha da tentativa anterior. */
  function handlePedirRemocao(alvo: AguardandoRemocao) {
    setErroDaRemocao(null);
    setARemover(alvo);
  }

  /** Fecha a confirmação de exclusão, com a falha anterior junto. */
  function handleRemocaoOpenChange(aberto: boolean) {
    if (aberto) return;
    setARemover(null);
    setErroDaRemocao(null);
  }

  /** Apaga o registro confirmado e fecha a caixa, se a API aceitar. */
  async function handleConfirmarRemocao() {
    if (aRemover === null) return;
    setErroDaRemocao(null);

    try {
      if (aRemover.tipo === 'plano') await excluirPlano.mutateAsync(aRemover.plano.id);
      else await excluirMedico.mutateAsync(aRemover.medico.id);
      setARemover(null);
    } catch (erro) {
      setErroDaRemocao(
        erro instanceof ErroDaApi
          ? erro.message
          : 'A exclusão falhou antes de chegar ao servidor.',
      );
    }
  }

  return (
    <div>
      <CabecalhoDoModulo
        titulo="Saúde"
        subtitulo="PLANO DE SAÚDE, CARTEIRINHAS E MÉDICOS DA FAMÍLIA"
      />

      <SecaoCadastro
        titulo="Plano de saúde da família"
        descricao={subtituloDosPlanos}
        rotuloNovo="+ Novo plano"
        onNovo={podeEditar ? () => setPainel({ modo: 'novo-plano' }) : undefined}
        carregando={consultaPlanos.isPending}
        erro={consultaPlanos.error}
        onTentarDeNovo={() => void consultaPlanos.refetch()}
        vazia={planos.length === 0}
        mensagemVazia="Nenhum plano cadastrado ainda."
      >
        <TabelaPlanos
          planos={planos}
          hoje={hoje}
          onEditar={
            podeEditar ? (plano) => setPainel({ modo: 'edicao-plano', plano }) : undefined
          }
          onRemover={
            podeEditar ? (plano) => handlePedirRemocao({ tipo: 'plano', plano }) : undefined
          }
          podeAlterarFoto={podeEditar}
        />
      </SecaoCadastro>

      <SecaoCadastro
        titulo="Agenda de médicos da família"
        rotuloNovo="+ Novo médico"
        onNovo={podeEditar ? () => setPainel({ modo: 'novo-medico' }) : undefined}
        carregando={consultaMedicos.isPending}
        erro={consultaMedicos.error}
        onTentarDeNovo={() => void consultaMedicos.refetch()}
        vazia={(consultaMedicos.data ?? []).length === 0}
        mensagemVazia="Nenhum médico cadastrado ainda."
      >
        <TabelaMedicos
          medicos={consultaMedicos.data ?? []}
          onEditar={
            podeEditar ? (medico) => setPainel({ modo: 'edicao-medico', medico }) : undefined
          }
          onRemover={
            podeEditar ? (medico) => handlePedirRemocao({ tipo: 'medico', medico }) : undefined
          }
        />
      </SecaoCadastro>

      <Dialog open={painel.modo !== 'fechado'} onOpenChange={handlePainelOpenChange}>
        <DialogContent>
          {painel.modo !== 'fechado' && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {painel.modo === 'novo-plano' && 'Novo plano'}
                  {painel.modo === 'edicao-plano' &&
                    `${painel.plano.plano} — ${painel.plano.pessoa.apelido}`}
                  {painel.modo === 'novo-medico' && 'Novo médico'}
                  {painel.modo === 'edicao-medico' && painel.medico.nome}
                </DialogTitle>
              </DialogHeader>

              {(painel.modo === 'novo-plano' || painel.modo === 'edicao-plano') && (
                <FormularioPlano
                  key={painel.modo === 'novo-plano' ? 'novo' : painel.plano.id}
                  plano={painel.modo === 'novo-plano' ? null : painel.plano}
                  onSalvo={() => setPainel({ modo: 'fechado' })}
                  onCancelar={() => setPainel({ modo: 'fechado' })}
                />
              )}

              {(painel.modo === 'novo-medico' || painel.modo === 'edicao-medico') && (
                <FormularioMedico
                  key={painel.modo === 'novo-medico' ? 'novo' : painel.medico.id}
                  medico={painel.modo === 'novo-medico' ? null : painel.medico}
                  onSalvo={() => setPainel({ modo: 'fechado' })}
                  onCancelar={() => setPainel({ modo: 'fechado' })}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={aRemover !== null} onOpenChange={handleRemocaoOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remover{' '}
              {aRemover?.tipo === 'plano'
                ? `${aRemover.plano.plano} de ${aRemover.plano.pessoa.nomeCompleto}`
                : aRemover?.medico.nome}
              ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {aRemover?.tipo === 'plano'
                ? 'A apólice sai do cadastro e deixa de ser anunciada no aviso de vencimento. Não há como desfazer.'
                : 'O médico sai da agenda. Não há como desfazer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {erroDaRemocao !== null && (
            <p className="text-sm text-destructive" role="alert">
              {erroDaRemocao}
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={removendo}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={removendo}
              onClick={(evento) => {
                // O `AlertDialogAction` fecha a caixa ao ser clicado, e fechá-la
                // antes da resposta da API desmontaria o estado de envio e
                // esconderia a falha. O fechamento passa a ser nosso.
                evento.preventDefault();
                void handleConfirmarRemocao();
              }}
            >
              {removendo ? 'Removendo…' : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
